import { copy } from '../../copy';
import type { MacroKey, Plan } from '../../types';

export function mixLabel(T: { p: number; c: number; f: number }) {
  const e = 4 * T.p + 4 * T.c + 9 * T.f;
  if (!e) return '—';
  const rp = Math.round((4 * T.p / e) * 100);
  const rc = Math.round((4 * T.c / e) * 100);
  const rf = Math.round((9 * T.f / e) * 100);
  return `${rp} / ${rc} / ${rf}`;
}

export function mixWidths(T: { p: number; c: number; f: number }) {
  const e = 4 * T.p + 4 * T.c + 9 * T.f;
  if (!e) return { p: '0%', c: '0%', f: '0%' };
  return {
    p: `${(4 * T.p / e) * 100}%`,
    c: `${(4 * T.c / e) * 100}%`,
    f: `${(9 * T.f / e) * 100}%`,
  };
}

export function mealSource(plan: Plan, m: Plan['meals'][0]) {
  if (plan.split === 'standard') return copy.targets.sourceStandard;
  if (m.ratio != null || m.options.some(o => o.foods.length)) return copy.targets.sourceTemplate;
  return copy.targets.sourceStandard;
}

export function shortMealName(name: string) {
  const n = name.toLowerCase();
  if (n.includes('early morning') || n.includes('early_morning')) return 'Early morning';
  if (n.includes('morning') && n.includes('snack')) return 'AM snack';
  if (n.includes('evening') && n.includes('snack')) return 'PM snack';
  if (n.includes('evening') || n.includes('pm ')) return 'PM snack';
  if (n.includes('bed')) return 'Bedtime';
  return name.split(' ')[0];
}

/** ~% of bar width needed for `text` at 11.5px with segment padding (hide if segment is narrower). */
function textFitsShareBar(widthPct: number, text: string) {
  if (!text) return true;
  return widthPct >= 2 + text.length * 0.85;
}

function mealShareBarNameCandidate(share: number, name: string, key?: string) {
  const n = name.toLowerCase();
  const snack =
    key === 'morning_snacks' ||
    key === 'evening_snacks' ||
    (n.includes('snack') && !n.includes('breakfast'));
  const min = snack || n.includes('early') || n.includes('bed') ? 5 : 10;
  return share >= min ? shortMealName(name) : '';
}

/** Labels for the meal-targets share bar; omitted when the segment is too narrow (no ellipsis). */
export function mealShareBarLabels(
  share: number,
  barDenom: number,
  name: string,
  key?: string,
): { label: string; pct: string } {
  const widthPct = barDenom > 0 ? (share / barDenom) * 100 : 0;
  const candidate = mealShareBarNameCandidate(share, name, key);
  const label = candidate && textFitsShareBar(widthPct, candidate) ? candidate : '';
  const pctStr = share >= 4 ? `${share}%` : '';
  const pct = pctStr && textFitsShareBar(widthPct, pctStr) ? pctStr : '';
  return { label, pct };
}

export const MACRO_KEYS: MacroKey[] = ['p', 'c', 'f'];

/** Footer total cell: red when shares ≠ 100%, orange when macro drifts, green when aligned. */
export function mealTargetsFootMacroProps(sharesOk: boolean, macroDrift: boolean) {
  if (!sharesOk) return { 'data-ok': false as const };
  if (macroDrift) return { 'data-warn': true as const };
  return { 'data-ok': true as const };
}
